import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import StageBadge from '../../../components/StageBadge';
import { useCurrency } from '../../../contexts/CurrencyContext';
import { calculateMortgagePayment } from '../../../utils/mortgageCalculations';

const PropertyHeader = ({ 
  property,
  isVisitor = false,
  subscriptionTier = 'free',
  onBack,
  onEdit,
  onExport,
  onShare,
  onCreateScenario,
  onPlanning,
  onPurchaseJourney,
  onStageChange,
  onConvertToAcquired,
  kpis
}) => {
  const canExportPdf = true;
  const canPlanning = true;
  const { formatCurrency } = useCurrency();
  const getPropertyTypeLabel = (type) => {
    const labels = {
      multiplex: 'Multiplex',
      condo: 'Condo locatif',
      shortterm: 'Court terme',
      commercial: 'Commercial',
      other: 'Autre'
    };
    return labels?.[type] || type;
  };

  // Calculate KPIs for display (same as dashboard property cards)
  const calculateKPIs = () => {
    if (!property && !kpis) return null;

    // Use kpis if available, otherwise calculate from property
    const monthlyCashflow = kpis?.monthlyCashflow || property?.monthly_cashflow || 0;
    const capRate = kpis?.capRate || property?.cap_rate || 0;
    // Try multiple formats for purchase price (same as PropertyCard in dashboard)
    const purchasePrice = property?.purchase_price || property?.purchasePrice || property?.price || 0;
    
    // Calculate ROI 10 years if not in kpis
    let roi10Year = kpis?.roiTotal;
    if (roi10Year === undefined && property) {
      // Simplified calculation - use property data
      const totalInvestment = property?.total_initial_investment || property?.down_payment_amount || 0;
      const annualCashflow = monthlyCashflow * 12;
      const appreciationRate = property?.appreciation_rate || 3;
      const rentGrowth = (property?.rent_growth_rate || 2) / 100;
      const expenseGrowth = 0.02;
      const baseRevenue = (property?.total_monthly_revenue || 0) * 12;
      const baseExpenses = (property?.total_monthly_expenses || 0) * 12;
      const amortizationYears = property?.amortization_period || 25;
      
      const mortgageAmount = property?.mortgage_amount || 0;
      const interestRate = property?.interest_rate || 0;
      const paymentFrequency = property?.payment_frequency || 'monthly';
      
      const mortgagePaymentCalc = calculateMortgagePayment(
        mortgageAmount,
        interestRate,
        amortizationYears,
        paymentFrequency
      );
      const annualMortgagePayment = mortgagePaymentCalc.annualPayment;
      
      const cumulativeCashflow10Years = Array.from({ length: 10 }, (_, j) => {
        const yRevenue = baseRevenue * Math.pow(1 + rentGrowth, j);
        const yExpenses = baseExpenses * Math.pow(1 + expenseGrowth, j);
        const yNOI = yRevenue - yExpenses;
        const yActualMortgagePayment = ((j + 1) > amortizationYears) ? 0 : annualMortgagePayment;
        return yNOI - yActualMortgagePayment;
      }).reduce((sum, cf) => sum + (cf || 0), 0);
      
      const futureValue = purchasePrice * Math.pow(1 + appreciationRate / 100, 10);
      const equityGain = futureValue - purchasePrice;
      roi10Year = totalInvestment > 0 
        ? ((cumulativeCashflow10Years + equityGain) / totalInvestment) * 100 
        : 0;
    }

    return {
      cashflow: monthlyCashflow,
      capRate,
      price: purchasePrice,
      roi10Year: roi10Year || 0
    };
  };

  const propertyKPIs = calculateKPIs();

  return (
    <div className="bg-card border border-border rounded-xl p-4 md:p-6 mb-4 md:mb-6">
      <div className="flex items-start gap-4 mb-4">
        <Button
          variant="ghost"
          size="icon"
          iconName="ArrowLeft"
          onClick={onBack}
          className="flex-shrink-0"
        />
        
        <div className="flex-1 min-w-0">
          <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-2">
            <h1 className="text-2xl md:text-3xl font-bold text-foreground truncate">
              {property?.address}
            </h1>
            {!isVisitor && (
              <StageBadge 
                stage={property?.stage || property?.status}
                onStageChange={onStageChange}
                propertyId={property?.id}
              />
            )}
            {isVisitor && property?.stage && (
              <span className="px-2.5 py-1 rounded-md bg-muted text-muted-foreground text-sm font-medium">
                {property.stage === 'analysis' && 'Analyse'}
                {property.stage === 'acquired' && 'Acquis'}
                {!['analysis', 'acquired'].includes(property.stage) && property.stage}
              </span>
            )}
          </div>
          
          <div className="flex flex-wrap items-center gap-3 md:gap-4 caption text-muted-foreground">
            <div className="flex items-center gap-2">
              <Icon name="MapPin" size={16} />
              <span>{property?.city}, {property?.province}</span>
            </div>
            <div className="flex items-center gap-2">
              <Icon name="Home" size={16} />
              <span>{getPropertyTypeLabel(property?.type)}</span>
            </div>
            {(property?.analysis_date || property?.analysisDate) && (
              <div className="flex items-center gap-2">
                <Icon name="Calendar" size={16} />
                <span>Analysé le {new Date(property.analysis_date || property.analysisDate).toLocaleDateString('fr-CA')}</span>
              </div>
            )}
            {property?.propertyUrl && (
              <a
                href={property.propertyUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-primary hover:underline"
              >
                <Icon name="ExternalLink" size={16} />
                <span>Voir la fiche</span>
              </a>
            )}
          </div>
        </div>


        <div className="hidden lg:block w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
          <Image 
            src={property?.mainImage} 
            alt={property?.mainImageAlt || property?.address || 'Photo de la propriété'}
            className="w-full h-full object-cover"
          />
        </div>
      </div>
      {!isVisitor && (
        <div className="flex flex-col sm:flex-row gap-2 md:gap-3 pt-4 border-t border-border/50">
          {canPlanning && (property?.stage === 'acquired' || property?.status === 'acquired') ? (
            <Button
              variant="default"
              iconName="CheckSquare"
              iconPosition="left"
              onClick={onPlanning}
              className="flex-1 sm:flex-initial"
            >
              Planification
            </Button>
          ) : !canPlanning && (property?.stage === 'acquired' || property?.status === 'acquired') ? null : (
            <>
              <Button
                variant="default"
                iconName="ShoppingCart"
                iconPosition="left"
                onClick={onPurchaseJourney}
                className="flex-1 sm:flex-initial"
              >
                Parcours d'achat
              </Button>
              <Button
                variant="outline"
                iconName="Home"
                iconPosition="left"
                onClick={onConvertToAcquired}
                className="flex-1 sm:flex-initial"
              >
                Marquer comme acquis
              </Button>
            </>
          )}
          <Button
            variant="outline"
            iconName="Edit"
            iconPosition="left"
            onClick={onEdit}
            className="flex-1 sm:flex-initial"
          >
            Modifier
          </Button>
          <div className="relative flex-1 sm:flex-initial">
            <Button
              variant="outline"
              iconName="Share2"
              iconPosition="left"
              onClick={onShare}
              className="w-full"
            >
              {property?.is_shared ? 'Publié' : 'Partager'}
            </Button>
            {property?.is_shared && (
              <span 
                className="absolute -top-1 -right-1 w-3 h-3 bg-green-500 rounded-full border-2 border-card"
                title="Cette analyse est publiée et accessible via un lien de partage"
              />
            )}
          </div>
          {canExportPdf && (
            <Button
              variant="outline"
              iconName="Download"
              iconPosition="left"
              onClick={onExport}
              className="flex-1 sm:flex-initial"
            >
              Exporter PDF
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default PropertyHeader;